import React, { Component } from "react";
import {
    MDBRow,
    MDBCol,
    MDBBtn,
    MDBIcon,
    MDBSpinner,
    toast,
} from "mdbreact";
import "./MultiTenancy.css";

/**
 * Secondary Operator Field — Mockup #1
 *
 * Drops into the venue create / edit form (e.g. ModifyVenue.js, AddVenueWizard.js).
 *
 * Props (all optional in prototype mode):
 *   primaryOperatorId           - the operator ID that owns the venue (for self-association check)
 *   currentAssociation          - { status, secondaryOperatorId, secondaryOperatorName, requestedAt, ... }
 *   onRequestAssociation(opId)  - called when Primary Operator confirms a new request
 *   onCancelRequest()           - called to cancel a pending request
 *   onRemoveAssociation()       - called to remove an active Secondary Operator
 *   lookupOperator(opId)        - async function returning { id, name, status } or null
 */
class SecondaryOperatorField extends Component {
    constructor(props) {
        super(props);
        this.state = {
            operatorIdInput: "",
            lookupResult: null,        // { id, name, status }
            lookupError: null,
            isLookingUp: false,
            isConfirming: false,
        };
    }

    handleInputChange = (e) => {
        this.setState({
            operatorIdInput: e.target.value,
            lookupResult: null,
            lookupError: null,
        });
    };

    handleLookup = async () => {
        const { operatorIdInput } = this.state;
        const { primaryOperatorId, lookupOperator } = this.props;

        if (!operatorIdInput || operatorIdInput.trim().length === 0) {
            this.setState({ lookupError: "Please enter an Operator ID." });
            return;
        }

        if (primaryOperatorId && operatorIdInput.trim() === primaryOperatorId) {
            this.setState({
                lookupError:
                    "You cannot add your own operator as a Secondary Operator.",
                lookupResult: null,
            });
            return;
        }

        this.setState({ isLookingUp: true, lookupError: null });

        try {
            // Prototype: fall back to a mock lookup if no real one is wired
            const result = lookupOperator
                ? await lookupOperator(operatorIdInput.trim())
                : await this.mockLookup(operatorIdInput.trim());

            if (!result) {
                this.setState({
                    lookupError:
                        "No operator found with that ID. Please check and try again.",
                    lookupResult: null,
                    isLookingUp: false,
                });
                return;
            }

            this.setState({ lookupResult: result, isLookingUp: false });
        } catch (err) {
            this.setState({
                lookupError: "Lookup failed. Please try again.",
                isLookingUp: false,
            });
        }
    };

    mockLookup = (id) => {
        // Stand-in for an API call so the prototype is interactive
        return new Promise((resolve) => {
            setTimeout(() => {
                const mockDirectory = {
                    "OP-7H4K-2891": {
                        id: "OP-7H4K-2891",
                        name: "Mereo Networks",
                        status: "Active",
                    },
                    "OP-3F9X-1024": {
                        id: "OP-3F9X-1024",
                        name: "RoamingiQ Demo",
                        status: "Active",
                    },
                    "OP-SMRT-AIRA": {
                        id: "OP-SMRT-AIRA",
                        name: "SmartAira",
                        status: "Active",
                    },
                };
                resolve(mockDirectory[id] || null);
            }, 600);
        });
    };

    handleConfirmRequest = () => {
        const { lookupResult } = this.state;
        const { onRequestAssociation } = this.props;

        if (!lookupResult) return;

        this.setState({ isConfirming: true });
        if (onRequestAssociation) {
            onRequestAssociation(lookupResult.id);
        } else {
            toast.success(
                `Association request sent to ${lookupResult.name}. They must approve before access becomes active.`,
                { closeButton: false }
            );
        }
        this.setState({
            isConfirming: false,
            operatorIdInput: "",
            lookupResult: null,
        });
    };

    renderStatusBadge = (status) => {
        const map = {
            NONE: { label: "No Secondary Operator", className: "status-none" },
            PENDING: { label: "Pending Approval", className: "status-pending" },
            ACTIVE: { label: "Active", className: "status-active" },
            REJECTED: { label: "Rejected", className: "status-rejected" },
            REMOVED: { label: "Removed", className: "status-removed" },
            SUSPENDED: { label: "Suspended", className: "status-suspended" },
            EXPIRED: { label: "Expired", className: "status-removed" },
        };
        const entry = map[status] || map.NONE;
        return (
            <span className={`mt-status-badge ${entry.className}`}>
                {entry.label}
            </span>
        );
    };

    renderCurrentAssociation = () => {
        const { currentAssociation, onCancelRequest, onRemoveAssociation } =
            this.props;

        if (!currentAssociation || currentAssociation.status === "NONE") {
            return (
                <div className="mt-current-status">
                    <strong>Current Status: </strong>
                    {this.renderStatusBadge("NONE")}
                </div>
            );
        }

        const {
            status,
            secondaryOperatorName,
            secondaryOperatorId,
            requestedAt,
            approvedAt,
        } = currentAssociation;

        return (
            <div className="mt-current-association">
                <MDBRow>
                    <MDBCol md="6">
                        <p>
                            <strong>Secondary Operator: </strong>
                            {secondaryOperatorName}
                        </p>
                        <p className="mt-muted">{secondaryOperatorId}</p>
                    </MDBCol>
                    <MDBCol md="6">
                        <p>
                            <strong>Status: </strong>
                            {this.renderStatusBadge(status)}
                        </p>
                        {status === "PENDING" && requestedAt && (
                            <p className="mt-muted">
                                Requested: {requestedAt}
                            </p>
                        )}
                        {status === "ACTIVE" && approvedAt && (
                            <p className="mt-muted">
                                Approved: {approvedAt}
                            </p>
                        )}
                    </MDBCol>
                </MDBRow>
                <div className="mt-action-row">
                    {status === "PENDING" && (
                        <MDBBtn
                            color="warning"
                            size="sm"
                            onClick={onCancelRequest}
                        >
                            Cancel Request
                        </MDBBtn>
                    )}
                    {status === "ACTIVE" && (
                        <MDBBtn
                            color="danger"
                            size="sm"
                            onClick={onRemoveAssociation}
                        >
                            <MDBIcon icon="user-minus" /> Remove Secondary
                            Operator
                        </MDBBtn>
                    )}
                </div>
            </div>
        );
    };

    render() {
        const {
            operatorIdInput,
            lookupResult,
            lookupError,
            isLookingUp,
            isConfirming,
        } = this.state;
        const { currentAssociation } = this.props;

        const hasActiveOrPending =
            currentAssociation &&
            ["PENDING", "ACTIVE"].includes(currentAssociation.status);

        return (
            <div className="mt-secondary-operator-field">
                <div className="mt-section-header">
                    <MDBIcon icon="handshake" />
                    <h5>Secondary Operator Association</h5>
                </div>

                {this.renderCurrentAssociation()}

                {!hasActiveOrPending && (
                    <>
                        <MDBRow className="mt-input-row">
                            <MDBCol md="8">
                                <label
                                    htmlFor="secondaryOperatorId"
                                    className="black-text"
                                >
                                    Secondary Operator ID
                                </label>
                                <input
                                    type="text"
                                    id="secondaryOperatorId"
                                    className="form-control"
                                    placeholder="OP-XXXX-XXXX"
                                    value={operatorIdInput}
                                    onChange={this.handleInputChange}
                                />
                            </MDBCol>
                            <MDBCol md="4" className="mt-lookup-btn-col">
                                <MDBBtn
                                    color="primary"
                                    onClick={this.handleLookup}
                                    disabled={
                                        isLookingUp ||
                                        operatorIdInput.length === 0
                                    }
                                >
                                    {isLookingUp ? (
                                        <MDBSpinner small />
                                    ) : (
                                        <>
                                            <MDBIcon icon="search" /> Look Up
                                        </>
                                    )}
                                </MDBBtn>
                            </MDBCol>
                        </MDBRow>

                        {lookupError && (
                            <div className="mt-alert mt-alert-error">
                                <MDBIcon icon="exclamation-circle" />{" "}
                                {lookupError}
                            </div>
                        )}

                        {lookupResult && (
                            <div className="mt-alert mt-alert-success">
                                <div className="mt-lookup-result">
                                    <p className="mt-lookup-title">
                                        <MDBIcon icon="check-circle" />{" "}
                                        Operator Found
                                    </p>
                                    <p>
                                        <strong>Operator Name: </strong>
                                        {lookupResult.name}
                                    </p>
                                    <p>
                                        <strong>Operator ID: </strong>
                                        {lookupResult.id}
                                    </p>
                                    <p>
                                        <strong>Status: </strong>
                                        {lookupResult.status}
                                    </p>
                                </div>
                            </div>
                        )}

                        <div className="mt-info-note">
                            <MDBIcon icon="info-circle" /> This operator will
                            receive a request to approve association with this
                            venue. They will not have access until approved.
                        </div>

                        <div className="mt-action-row">
                            <MDBBtn
                                color="success"
                                onClick={this.handleConfirmRequest}
                                disabled={!lookupResult || isConfirming}
                            >
                                {isConfirming ? (
                                    <MDBSpinner small />
                                ) : (
                                    <>
                                        <MDBIcon icon="paper-plane" /> Request
                                        Association
                                    </>
                                )}
                            </MDBBtn>
                        </div>
                    </>
                )}
            </div>
        );
    }
}

export default SecondaryOperatorField;
